<template>
  <UiLink v-if="to" :to="to" class="cn-button" :class="classes">
    <UiButtonInner :icon="icon" :variant="variant">
      <slot />
    </UiButtonInner>
  </UiLink>
  <button
    v-else
    :type="type"
    :disabled="disabled"
    :class="classes"
    class="cn-button"
    @click="clickEvent"
  >
    <UiButtonInner :icon="icon" :variant="variant">
      <slot />
    </UiButtonInner>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import UiLink from '@/components/ui/UiLink';
import UiButtonInner from '@/components/ui/UiButtonInner';

export type ButtonVariant =
  'gray' | 'red' | 'aqua' | 'yellow' | 'blue' | 'red-outlined' | 'pink';

interface Props {
  variant?: ButtonVariant;
  icon?: string;
  type?: 'button' | 'submit' | 'reset';
  to?: string | null;
  block?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'red',
  icon: undefined,
  type: 'button',
  to: null,
  block: false,
  disabled: false,
});
const emit = defineEmits<{ (e: 'click'): void }>();

const clickEvent = () => {
  emit('click');
};

const classes = computed(() => [
  props.block && 'cn-button--block',
  props.disabled && 'cn-button--disabled',
]);
</script>

<style>
.cn-button {
  --ab-canyon-button-color-bg-accent: var(--ab-canyon-color-accent);
  --ab-canyon-button-color-bg-light: var(--ab-canyon-color-light);
  --ab-canyon-button-color-bg-neutral: var(--ab-canyon-color-neutral);
  --ab-canyon-button-color-text-light: var(--ab-canyon-color-light);
  --ab-canyon-button-color-text-dark: var(--ab-canyon-color-dark);

  --ab-canyon-button-color-bg-primary: var(
    --ab-canyon-color-primary,
    var(--ab-canyon-button-color-bg-accent)
  );
  --ab-canyon-button-color-bg-accent-yellow: var(
    --ab-canyon-color-accent-2,
    var(--ab-canyon-color-accent)
  );
  --ab-canyon-button-color-bg-accent-pink: var(
    --ab-canyon-color-accent-3,
    var(--ab-canyon-color-accent)
  );
  --ab-canyon-button-color-bg-accent-aqua: var(
    --ab-canyon-color-accent-4,
    var(--ab-canyon-color-accent)
  );

  position: relative;
  display: inline-block;
}

.cn-button--block {
  width: 100%;
}

.cn-button--disabled {
  cursor: not-allowed;
  opacity: 0.4;
}
</style>
